import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { ExternalLink, AlertCircle, CheckCircle2, XCircle, Clock, User, Mail, Calendar, MapPin, MessageSquare, Link as LinkIcon, Activity } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";


interface TargetDetailDialogProps {
  targetId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Sanitize error messages - hide technical details from workspace users
const sanitizeErrorMessage = (message: string | null): string => {
  if (!message) return 'Processing issue';

  const lowerMsg = message.toLowerCase();

  // Map technical errors to user-friendly messages
  if (lowerMsg.includes('profile') && (lowerMsg.includes('fetch') || lowerMsg.includes('inaccessible')))
    return 'Could not access this profile';
  if (lowerMsg.includes('already_invited') || lowerMsg.includes('already invited'))
    return 'Connection request already sent';
  if (lowerMsg.includes('rate') || lowerMsg.includes('limit') || lowerMsg.includes('throttl'))
    return 'Temporarily paused - will retry automatically';
  if (lowerMsg.includes('timeout'))
    return 'Connection timed out';
  if (lowerMsg.includes('not found') || lowerMsg.includes('deleted') || lowerMsg.includes('404'))
    return 'Profile no longer available';
  if (lowerMsg.includes('private') || lowerMsg.includes('restricted'))
    return 'Profile is private or restricted';
  if (lowerMsg.includes('invalid') && lowerMsg.includes('recipient'))
    return 'Could not reach this profile';
  if (lowerMsg.includes('sequence stopped') || lowerMsg.includes('previous step'))
    return 'Sequence stopped due to earlier issue';
  if (lowerMsg.includes('campaign not active'))
    return 'Campaign was paused';
  if (lowerMsg.includes('target replied'))
    return 'Target replied - sequence completed';
  if (lowerMsg.includes('deferred') || lowerMsg.includes('spacing'))
    return 'Scheduled for later (spacing rules)';

  // Generic fallback - don't show technical details
  return 'Processing issue occurred';
};

export function TargetDetailDialog({ targetId, open, onOpenChange }: TargetDetailDialogProps) {
  const { isSuperAdmin } = useUserRole();
  const location = useLocation();

  const { data: target, isLoading: targetLoading } = useQuery({
    queryKey: ['target-detail', targetId],
    queryFn: async () => {
      if (!targetId) return null;
      const { data, error } = await supabase
        .from('targets')
        .select('*')
        .eq('id', targetId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open && !!targetId
  });

  const { data: actions, isLoading: actionsLoading } = useQuery({
    queryKey: ['target-actions', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      const { data, error } = await supabase
        .from('action_log')
        .select('id, action_type, status, created_at, error_message, target_id')
        .eq('target_id', targetId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: open && !!targetId
  });

  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['target-jobs', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      const { data, error } = await supabase
        .from('job_queue')
        .select('*')
        .eq('target_id', targetId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: open && !!targetId
  });

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Helper to render milestones
  const renderMilestone = (label: string, date: string | null | undefined, icon: React.ReactNode) => {
    const isCompleted = !!date;
    return (
      <div className={`flex flex-col items-center text-center space-y-2 flex-1 p-2 rounded-lg ${isCompleted ? 'bg-primary/5' : 'opacity-50'}`}>
        <div className={`p-2 rounded-full ${isCompleted ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs font-medium">{label}</p>
          {date ? (
            <p className="text-[10px] text-muted-foreground">{new Date(date).toLocaleDateString()}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground">-</p>
          )}
        </div>
      </div>
    );
  };

  // Calculate message sent time from target data OR activity log
  // We check case-insensitive for action types and statuses to be robust
  let messageSentAt = target?.first_message_sent_at || 
                      target?.last_message_sent_at || 
                      actions?.find(a => 
                        (a.action_type?.toLowerCase() === 'send_message' || a.action_type?.toLowerCase() === 'send_inmail') && 
                        (a.status?.toLowerCase() === 'success' || a.status?.toLowerCase() === 'completed')
                      )?.created_at;

  // Fallback: If target has replied but we missed the message timestamp, 
  // light up the "Messaged" node using the reply time (or connection time) to maintain funnel continuity.
  if (!messageSentAt && target?.reply_received_at) {
    messageSentAt = target.reply_received_at;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0">
        {/* Header */}
        <div className="p-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-background shadow-sm">
                <AvatarImage src={(target?.custom_data as any)?.profile_picture_url} />
                <AvatarFallback className="text-lg bg-primary/10 text-primary">
                  {target?.first_name?.[0]}{target?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  {target?.first_name || target?.name || 'Unknown'} {target?.last_name || ''}
                </DialogTitle>
                <p className="text-muted-foreground font-medium">{target?.title || 'No Title'}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <Activity className="w-3 h-3" />
                    {target?.company || 'No Company'}
                  </span>
                  {target?.profile_url && (
                    <>
                      <span>•</span>
                      <a
                        href={target.profile_url}
                        className="text-primary hover:underline inline-flex items-center gap-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LinkedIn <ExternalLink className="w-3 h-3" />
                      </a>
                    </>
                  )}
                  {isSuperAdmin && target?.provider_id && (
                    <>
                      <span>•</span>
                      <Link
                        to={`/admin/target-database/detail/${encodeURIComponent(target.provider_id)}?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        Open full profile <ExternalLink className="w-3 h-3" />
                      </Link>
                    </>
                  )}
                </div>

              </div>
            </div>
            {target?.status && <StatusBadge status={target.status as any} />}
          </div>
        </div>

        {targetLoading ? (
          <div className="flex-1 flex items-center justify-center min-h-[300px]">
            <p className="text-muted-foreground">Loading details...</p>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden">
            <div className="px-6 pt-2">
              <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent gap-6">
                <TabsTrigger
                  value="overview"
                  className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:text-primary border-b-2 border-transparent rounded-none px-2 py-3"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="activity"
                  className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:text-primary border-b-2 border-transparent rounded-none px-2 py-3"
                >
                  Activity Log
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto bg-muted/5 p-6">
              <TabsContent value="overview" className="space-y-6 mt-0">
                {/* Milestones */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Journey</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between gap-4">
                      {renderMilestone('Sent', target?.connection_sent_at, <User className="w-4 h-4" />)}
                      <div className="h-px bg-border flex-1 mx-2" />
                      {renderMilestone('Connected', target?.connection_accepted_at, <LinkIcon className="w-4 h-4" />)}
                      <div className="h-px bg-border flex-1 mx-2" />
                      {renderMilestone('Messaged', messageSentAt, <MessageSquare className="w-4 h-4" />)}
                      <div className="h-px bg-border flex-1 mx-2" />
                      {renderMilestone('Replied', target?.reply_received_at, <MessageSquare className="w-4 h-4 fill-current" />)}
                    </div>
                  </CardContent>
                </Card>

                {/* Failure Alert */}
                {target?.status === 'failed' && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex gap-3 items-start">
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-destructive mb-1">Campaign Stopped</h4>
                      <p className="text-sm font-medium">{sanitizeErrorMessage(target.failure_reason)}</p>
                      {target.failed_at && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Stopped at: {new Date(target.failed_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Status Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Campaign Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b last:border-0">
                        <span className="text-sm text-muted-foreground">Current Step</span>
                        <Badge variant="secondary">Step {target?.current_sequence_step || 0}</Badge>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b last:border-0">
                        <span className="text-sm text-muted-foreground">Created</span>
                        <span className="text-sm font-medium">{target?.created_at ? new Date(target.created_at).toLocaleDateString() : '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b last:border-0">
                        <span className="text-sm text-muted-foreground">Last Action</span>
                        <span className="text-sm font-medium">{target?.last_action_at ? new Date(target.last_action_at).toLocaleDateString() : '-'}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Latest Interaction / Reply */}
                  <Card className="flex flex-col">
                    <CardHeader>
                      <CardTitle className="text-base">Latest Interaction</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 flex-1">
                      {target?.last_reply_content ? (
                        <div className="bg-muted/30 p-3 rounded-lg border text-sm italic text-muted-foreground relative">
                          <MessageSquare className="w-4 h-4 absolute top-3 left-3 text-muted-foreground/50" />
                          <div className="pl-6">
                            "{target.last_reply_content}"
                          </div>
                          <p className="text-right text-[10px] text-muted-foreground mt-2">
                            {target.reply_received_at ? new Date(target.reply_received_at).toLocaleString() : ''}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full py-4 text-muted-foreground">
                          <MessageSquare className="w-8 h-8 mb-2 opacity-20" />
                          <p className="text-sm">No replies received yet</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="activity" className="space-y-6 mt-0">
                {/* Pending Actions */}
                {jobs && jobs.filter(j => j.status === 'pending').length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Up Next</h3>
                    {jobs.filter(j => j.status === 'pending').map(job => (
                      <div key={job.id} className="bg-background border rounded-lg p-3 flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-50 text-blue-600 rounded-full">
                            <Clock className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium text-sm capitalize">{job.job_type.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">Scheduled for {new Date(job.scheduled_for).toLocaleString()}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">Pending</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {/* History */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">History</h3>
                  {actions && actions.length > 0 ? (
                    <div className="relative border-l-2 border-muted ml-3 space-y-6 pb-2">
                      {actions.map((action, i) => (
                        <div key={action.id} className="ml-6 relative">
                          <div className={`absolute -left-[31px] top-0 p-1.5 rounded-full border-2 border-background ${
                            action.status === 'success' ? 'bg-green-100 text-green-600' : 
                            action.status === 'failed' ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'
                          }`}>
                            {action.status === 'success' ? <CheckCircle2 className="w-3.5 h-3.5" /> : 
                             action.status === 'failed' ? <XCircle className="w-3.5 h-3.5" /> : <Activity className="w-3.5 h-3.5" />}
                          </div>
                          <div className="bg-background border rounded-lg p-3 shadow-sm">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-sm capitalize">{action.action_type.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-muted-foreground">{new Date(action.created_at).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={action.status === 'success' ? 'secondary' : action.status === 'failed' ? 'destructive' : 'outline'} className="text-[10px] px-1.5 py-0 h-5">
                                {action.status}
                              </Badge>
                            </div>
                            {action.error_message && action.status !== 'success' && (
                              <p className="text-xs text-destructive mt-2 bg-destructive/5 p-2 rounded">
                                {sanitizeErrorMessage(action.error_message)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground text-sm bg-background border rounded-lg border-dashed">
                      No past activity recorded
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
